import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import cn from 'classnames';
import style from './style.module.scss';
import { Order } from '../../../services';
import { useClickOutside } from "../../../hooks/useClickOutside";
import { requestErrorDisplay } from '../../../utils';
import OrderItemFilter from '../section-item/components/OrderItemFilter';
import ExportOrders from '../section-item/components/ExportOrders';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const OrdersTab = ({
  setStatus,
  changeSortColumn,
  sortColumn = null,
  onDownloadExcel,
  isCallTaps = false,
  cityFrom,
  cityTo,
  loadingDate,
  unloadingDate,
  selectedStatus,
  statusActive,
}) => {
  const currentUrl = new URL(window.location);
  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const [searchParams] = useSearchParams();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownContent = useRef(null);
  const { t } = useTranslation();

  useClickOutside(dropdownContent, () => setDropdownOpen(false));

  const STATUS = {
    "all": { name: t('common.status.tabsList.all')},
    "published": { name: t('common.status.tabsList.published')},
    "booked": { name: t('common.status.tabsList.booked')},
    "onthetrip": { name: t('common.status.tabsList.onthetrip')},
    "completed": { name: t('common.status.tabsList.completed')},
  };

  const updateActiveStatus = (item = {}, status = 'all', isInit = false) => {
    setActiveStatus(item);
    if (!isInit) {
      currentUrl.searchParams.set("status", status);
      window.history.replaceState({}, "", currentUrl);
    }
    setStatus(item);
  };

  const getQueryParams = (data = []) => {
    const queryStatus = searchParams.get('status');

    if (!queryStatus) {
      updateActiveStatus(data[0], data[0].status, true);
      return;
    }

    const dataIndex = data.findIndex((d) => d.status === queryStatus);
    updateActiveStatus(data[dataIndex], queryStatus, true);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    const params = {
      status: !statusActive || statusActive.status === "all" ? "" : statusActive.status,
      cityIdFrom: cityFrom ? cityFrom.id : "",
      cityIdTo: cityTo ? cityTo.id : "",
      shippingDate: loadingDate ? moment(loadingDate).format("YYYY-MM-DD") : "",
      unloadingDate: unloadingDate ? moment(unloadingDate).format("YYYY-MM-DD") : "",
    };

    if (selectedStatus && statusActive.status === "all")
      params.status = selectedStatus.value;

    try {
      const response = await Order.getOwnOrderQuantityNew(params);
      if (response?.data) {
        setItems(response.data);
        getQueryParams(response.data);
      }
    } catch (e) {
      requestErrorDisplay(e)
    } finally {
      setLoading(false);
    }
  }, [cityFrom, cityTo, loadingDate, unloadingDate, selectedStatus, statusActive]);

  useEffect(() => {
    fetchData();
  }, [isCallTaps, cityFrom, cityTo, loadingDate, unloadingDate, selectedStatus]);

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  return (
    <div className={cn(style.wrapper)}>
      {/* Dropdown отображается только для мобильной версии */}
      <div ref={dropdownContent} className={style.dropdownWrapper}>
        <div className={style.flex}>
          <span className={style.statusTitle}>Статус:</span>
          <button
            type="button"
            className={cn(style.button, 'd-flex align-items-center', {
              [style.button_active]: activeStatus?.status,
            })}
            onClick={toggleDropdown}
          >
            {activeStatus?.status && STATUS[activeStatus?.status].name}
            <span className={cn(style.count, {
                // [style.count_active]: activeStatus?.status,
            })}>
              {activeStatus?.quantity || 0}
            </span>
            <div className={style.imgSelect}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
                className={style.checkmark}
              >
                <path
                  d="M1 1.5L5 5.5L9 1.5"
                  stroke="#909195"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
        {isDropdownOpen && (
          <div className={style.dropdownContent}>
            {items.map((item) => (
              <button
                type="button"
                key={"dropdown-" + item.status}
                className={cn(style.button, 'd-flex align-items-center', {
                  [style.button_none]: item.status === activeStatus?.status,
                })}
                onClick={() => {
                  toggleDropdown();
                  updateActiveStatus(item, item.status);
                }}
              >
                {item.status && STATUS[item.status].name}
                <span className={style.count}>
                  {item.quantity}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Код для не мобильной версии остается без изменений */}
      <div className={style.mobile}>
        {items.map((button) => (
          <button
            type="button"
            key={"desktop-" + button.status}
            className={cn(style.button, 'd-flex align-items-center text-nowrap', {
              [style.button_active]: button.status === activeStatus?.status,
            })}
            onClick={() => updateActiveStatus(button, button.status)}
          >
            {button.status && STATUS[button.status].name}
            <span className={cn(style.count, {
                [style.count_active]: button.status === activeStatus?.status,
            })}>{button.quantity}
            </span>
          </button>
        ))}
      </div>

      <OrderItemFilter
        changeSortColumn={changeSortColumn}
        sortColumn={sortColumn}
      />
      <ExportOrders onDownloadExcel={onDownloadExcel} />
    </div>
  );
};

export default OrdersTab;